import { FunctionComponent, useEffect, useMemo, useState } from 'react'

import styled, { css } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import useViewportDimensions from '@hooks/useViewport/useViewportDimensions'
import useViewportType from '@hooks/useViewport/useViewportType'
import { TextboxImageProps } from '@widgets/ArticleTextbox/types'
import FreeRatioImage from '@components/FreeRatioImage'
import { calculateSingleConstraintDimensions } from '@components/FreeRatioImage/utils'

interface StyledTextboxImageProps {
  mobileWidth: number
  mobileHeight: number
}

const StyledTextboxImage = styled.div<StyledTextboxImageProps>`
  ${({ mobileWidth, mobileHeight }) => {
    return css`
      width: auto;
      height: auto;

      ${mobileCSS(css`
        width: ${mobileWidth}px;
        height: ${mobileHeight}px;
        > * {
          width: 100%;
          height: 100%;
        }
        picture > img {
          width: 100%;
          height: 100%;
        }
      `)}
    `
  }}
`

const TextboxEntityImage: FunctionComponent<TextboxImageProps> = ({
  children: { src, alt, width, height, crops },
  imageDark,
}) => {
  const freeCrop = crops?.['free']
  const freeCropInclusiveWidth = freeCrop?.width ?? width
  const freeCropInclusivelHeight = freeCrop?.height ?? height
  const freeCropDark = imageDark?.src ? imageDark.crops?.['free'] : freeCrop
  const freeCropInclusiveWidthDark = freeCropDark?.width ?? width
  const freeCropInclusivelHeightDark = freeCropDark?.height ?? height

  const originalDimensions = useMemo(
    () => ({ width: freeCropInclusiveWidth, height: freeCropInclusivelHeight }),
    [freeCropInclusiveWidth, freeCropInclusivelHeight],
  )
  const originalDimensionsDark = useMemo(
    () => ({
      width: freeCropInclusiveWidthDark,
      height: freeCropInclusivelHeightDark,
    }),
    [freeCropInclusiveWidthDark, freeCropInclusivelHeightDark],
  )

  const maxWidthDesktop = 580
  const maxWidthTablet = 584
  const maxWidthMobile = 412
  const maxWidthMobileSmall = 320

  const viewportType = useViewportType()
  const { width: viewportWidth } = useViewportDimensions()

  const [actualMobileWidth, setActualMobileWidth] = useState<number>(0)
  const [actualMobileHeight, setActualMobileHeight] = useState<number>(0)

  useEffect(() => {
    if (viewportType === 'mobile') {
      const spacingAround = 2 * (20 + 12)
      const constraintWidth = viewportWidth - spacingAround
      const { width: calculatedMobileWidth, height: calculatedMobileHeight } =
        calculateSingleConstraintDimensions({
          constraint: {
            width: constraintWidth,
            height: originalDimensions.height,
          },
          originalDimensions,
        })
      setActualMobileWidth(calculatedMobileWidth)
      setActualMobileHeight(calculatedMobileHeight)
    }
  }, [originalDimensions, viewportType, viewportWidth])

  return (
    <StyledTextboxImage
      mobileWidth={actualMobileWidth}
      mobileHeight={actualMobileHeight}
    >
      <FreeRatioImage
        src={src ?? ''}
        alt={alt ?? ''}
        originalDimensions={originalDimensions}
        constraints={{
          desktop: {
            width: maxWidthDesktop,
            height: originalDimensions.height,
          },
          tablet: { width: maxWidthTablet, height: originalDimensions.height },
          mobile: { width: maxWidthMobile, height: originalDimensions.height },
          mobileSmall: {
            width: maxWidthMobileSmall,
            height: originalDimensions.height,
          },
        }}
        crops={crops ?? {}}
        {...(imageDark?.src
          ? {
              darkModeImage: {
                src: imageDark.src,
                crops: imageDark.crops ?? {},
                originalDimensions: originalDimensionsDark,
              },
            }
          : {})}
      />
    </StyledTextboxImage>
  )
}

export default TextboxEntityImage
